import {
  AfterViewInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { TableColumnModel } from '@shared/components/custom-table/models/TableColumn.model';
import { Subscription } from 'rxjs';
import { MediaChange, MediaObserver } from '@ngbracket/ngx-layout';
import { TableFilterRowDefDirective } from './directives/table-filter-row-def.directive';
import { ActionButtonModel } from '@shared/components/custom-table/models/action-button.model';
import { ConversationType } from '@shared/configs/conversation-types.config';
import { AccessService } from '@core/services/access.service';
import { PermissionStringEnum } from '@shared/enums';
import { CustomSnackbarService } from '@core/services/custom-snackbar.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { BankIoRetailUrl } from '@core/constants';
import { Router } from '@angular/router';

type sortOrderType = 'asc' | 'desc';

@Component({
  selector: 'app-custom-table',
  templateUrl: './custom-table.component.html',
  styleUrls: ['./custom-table.component.scss'],
})
export class CustomTableComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  currentScreenWidth = '';
  flexMediaWatcher: Subscription;
  conversationType = ConversationType;
  expandedElement: null;
  isExpanded = false;

  public tableDataSource = new MatTableDataSource([]);
  public displayedColumns: string[];
  public show = true;
  public page: number;
  isDeletionPermission: boolean;
  hasContentEditPermission: boolean;
  hasSchedulingPermission: boolean;
  disableEditing: boolean;
  totalCategoryRecords = 0;
  minRank = 0;
  maxRank = 0;
  permission = PermissionStringEnum.AuditReadOnly;
  @ViewChild(MatPaginator, { static: true }) matPaginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) matSort: MatSort;

  @Input() isPageable = false;
  @Input() isSortable = false;
  @Input() isFilterable = false;
  @Input() disableClientSideSorting = false; // For server-side sorting
  @Input() isTabletView = false;
  @Input() tableColumns: TableColumnModel[] = [];
  @Input() paginationSizes: number[] = [5, 10, 15];
  @Input() defaultPageSize = this.paginationSizes[1];
  @Input() sortBy: string | string[] = '';
  @Input() sortOrder: sortOrderType = 'asc';
  @Input() totalCount: number;
  @Input() pageIndex: number;
  @Input() emptyMsg: string;
  @Input() title: string;
  @Input() tableClassName: string;
  @Input() gradientTitle: boolean;
  @Input() actionBtnConfig: ActionButtonModel;
  @Input() footerTemplate: TemplateRef<any>;
  @Input() hasTableHorizontalScroll: boolean;
  @Input() filterClassName: string;
  @Input() showSearchBtn = true;
  @Input() categoryTableData: any[] = [];

  @Input() getAvailableOptionsFn?: (
    element: any,
    currentIndex: number
  ) => any[];
  @Input() getAvailableProductOptionsFn!: (
    element: any,
    currentIndex: number
  ) => any[];

  /** Optional: return array of dataKeys that are invalid for this row (e.g. for red-border validation) */
  @Input() getRowInvalidFields: (row: any) => string[] | undefined;

  @Output() downloadDocument: EventEmitter<any> = new EventEmitter<any>();
  @Output() previewDocument: EventEmitter<any> = new EventEmitter<any>();
  @Output() signDocument: EventEmitter<any> = new EventEmitter<any>();

  @Output() sort: EventEmitter<any> = new EventEmitter<string | string[]>();
  @Output() pageChanged: EventEmitter<any> = new EventEmitter<PageEvent>();
  @Output() rowClickAction: EventEmitter<any> = new EventEmitter<any>();
  @Output() accordionLinkClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() clickBtn: EventEmitter<any> = new EventEmitter<any>();
  @Output() clickDelete: EventEmitter<any> = new EventEmitter<any>();
  @Output() clickArchive: EventEmitter<any> = new EventEmitter<any>();
  @Output() clickRestore: EventEmitter<any> = new EventEmitter<any>();
  @Output() clickColumn: EventEmitter<any> = new EventEmitter<any>();
  @Output() clickEdit: EventEmitter<any> = new EventEmitter<any>();
  @Output() clickView: EventEmitter<any> = new EventEmitter<any>();
  @Output() clickViewChangeLog: EventEmitter<any> = new EventEmitter<any>();
  @Output() clickProducts: EventEmitter<any> = new EventEmitter<any>();
  @Output() import: EventEmitter<any> = new EventEmitter<any>();
  @Output() downloadElement: EventEmitter<any> = new EventEmitter<any>();
  @Output() downloadElementXML: EventEmitter<any> = new EventEmitter<any>();
  @Output() downloadElementPDF: EventEmitter<any> = new EventEmitter<any>();
  @Output() downloadElementJSON: EventEmitter<any> = new EventEmitter<any>();
  @Output() skipRescedule: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectedCheckbox: EventEmitter<any> = new EventEmitter<any>();
  @Output() sendActivation: EventEmitter<any> = new EventEmitter<any>();
  @Output() resetPassword: EventEmitter<any> = new EventEmitter<any>();
  @Output() rankUp = new EventEmitter<any>();
  @Output() rankDown = new EventEmitter<any>();
  @Output() rowSelected: EventEmitter<any> = new EventEmitter<any>();
  @Output() readMoreClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() inputChanged = new EventEmitter<{ element: any; column: any }>();
  @Output() checkboxChanged = new EventEmitter<any>();

  @Input() parentData: any;
  isRetailBranchApplication: boolean;
  @Input() set tableData(data: any[]) {
    this.tableDataSource.data = data || [];
    this.totalCategoryRecords = this.tableDataSource.data.length || 0;
    if (this.disableClientSideSorting && this.tableDataSource.sort) {
      this.tableDataSource.sort = null;
    }

    if (data && data.length > 0) {
      const ranksOrOrders = data.map(c => c.rank ?? c.order).filter(v => v != null);

      if (ranksOrOrders.length > 0) {
        this.minRank = Math.min(...ranksOrOrders);
        this.maxRank = Math.max(...ranksOrOrders);
      } else {
        this.minRank = 0;
        this.maxRank = 0;
      }
    } else {
      this.minRank = 0;
      this.maxRank = 0;
    }
  }

  @ContentChildren(TableFilterRowDefDirective, { read: TemplateRef })
  filterRowDefs: QueryList<TemplateRef<any>>;

  constructor(
    private mediaObserver: MediaObserver,
    private accessService: AccessService,
    private toastService: CustomSnackbarService,
    private clipboard: Clipboard,
    private sanitizer: DomSanitizer,
    private router: Router,
  ) {
    this.tableDataSource = new MatTableDataSource([]);
  }
isObservable(value: any): boolean {
  return value && typeof value.subscribe === 'function';
}

  setupTable(): void {
    this.displayedColumns = this.tableColumns?.map(
      (tableColumn: TableColumnModel) => tableColumn.name
    );
    this.show = true;
    if (['md', 'sm', 'xs'].includes(this.currentScreenWidth)) {
      this.show = !this.show;
      this.displayedColumns = [];
      this.tableColumns?.forEach((tableColumn: TableColumnModel) => {
        if (!tableColumn.hide) {
          this.displayedColumns.push(tableColumn.name);
        }
      });
    }
  }

  ngOnInit(): void {
    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .subscribe((changes: MediaChange[]) => {
        const currentChange = changes[0];
        if (currentChange.mqAlias !== this.currentScreenWidth) {
          this.currentScreenWidth = currentChange.mqAlias;
          this.setupTable();
        }
      });
    this.isDeletionPermission = this.accessService.checkUserPermissions([
      PermissionStringEnum.PermanentlyDeleteFiles,
    ]);
    this.isRetailBranchApplication = this.accessService.checkUserPermissions([
      PermissionStringEnum.RetailBranchApplication,
    ]);
    this.hasSchedulingPermission = this.accessService.checkUserPermissions([
      PermissionStringEnum.ConfigureQueryScheduling,
    ]);
    this.disableEditing = this.accessService.checkUserPermissions([
      PermissionStringEnum.AuditReadOnly,
    ]);
    this.hasContentEditPermission = this.accessService.checkUserPermissions([
      PermissionStringEnum.ManageContent,
    ]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.currentValue !== undefined &&
      changes.pageIndex?.currentValue !== undefined &&
      changes.pageIndex.currentValue - 1 !== this.page
    ) {
      this.page = changes.pageIndex.currentValue;
    }

    if (typeof this.parentData === 'number') {
      this.page =
        +this.parentData === 0 ? this.parentData : this.parentData - 1;
    }

    if (changes.tableColumns && changes.tableColumns.currentValue) {
      this.setupTable();
    }
  }

  ngOnDestroy(): void {
    this.flexMediaWatcher.unsubscribe();
  }

  ngAfterViewInit(): void {
    if (!this.disableClientSideSorting) {
      this.tableDataSource.sort = this.matSort;
    } else {
      this.tableDataSource.sort = null;
      this.tableDataSource.sortingDataAccessor = (data: any, sortHeaderId: string) => {
        return data[sortHeaderId];
      };
    }
  }

  sortTable(sortParameters: Sort): void {
    const column = this.tableColumns?.find(
      (column) => column.name === sortParameters.active
    );
    const columnValueForSort =
      column.sortColumnValue || (column.dataKey as string);

    sortParameters.active = columnValueForSort;
    this.sort.emit(sortParameters);
  }

  pageChange(state: PageEvent): void {
    this.page = state.pageIndex;
    this.pageChanged.emit(state);
  }

  isOverdue(element): string {
    if (element && element.dueDate) {
      return element.dueDate.indexOf('overdue') !== -1 ? 'overdue-date' : '';
    }
    return '';
  }

  clickRow(event): void {
    this.rowClickAction.emit(event);
  }

  accordionClick(event): void {
    this.accordionLinkClick.emit(event);
  }

  startImport(event): void {
    this.import.emit(event);
  }

  download(element): void {
    this.downloadElement.emit(element);
  }

  skipRescheduleBtn(element): void {
    this.skipRescedule.emit(element);
  }

  isClickable(): boolean {
    return this.rowClickAction.observers.length > 0;
  }

  onSelectRow(element: any, event: Event): void {
    event.stopPropagation();
    console.log('Selected Row:', element);

    this.rowSelected.emit(element);
  }

  clickActionBtn(event): void {
    this.clickBtn.emit(event);
  }

  selectCheckbox(element): void {
    setTimeout(() => {
      this.selectedCheckbox.emit(element);
    }, 0);
  }

  delete(data): void {
    this.clickDelete.emit(data);
  }

  archive(data): void {
    this.clickArchive.emit(data);
  }
  restore(data): void {
    this.clickRestore.emit(data);
  }

  clickedColumn(data): void {
    this.clickColumn.emit(data);
  }

  edit(data, event: Event): void {
    this.clickEdit.emit({ data, event });
  }

  getFullName(element): string {
    if (!element) {
      return '';
    }
    return `${element.firstName || ''} ${element.lastName || ''}`.trim();
  }

  isColumnVisible(column: TableColumnModel): boolean {
    return column.visible === undefined || column.visible;
  }

  sendactivation(data): void {
    this.sendActivation.emit(data);
  }

  resetpassword(data): void {
    this.resetPassword.emit(data);
  }

  hasPermissionForRedirection(): boolean {
    return this.accessService.checkUserPermissions([
      PermissionStringEnum.AuditReadOnly,
      PermissionStringEnum.RetailApplicationMaintenance,
    ]);
  }

  onDownloadClick(element: any, signerId: string): void {
    this.downloadDocument.emit({ element, signerId });
  }

  onPreviewClick(element: any, signerId: string): void {
    this.previewDocument.emit({ element, signerId });
  }

  onSignDocumentClick(element: any, signerId: string): void {
    this.signDocument.emit({ element, signerId });
  }

  view(data): void {
    this.clickView.emit({ data });
  }

  viewChangeLog(data): void {
    this.clickViewChangeLog.emit({ data });
  }

  products(data): void {
    this.clickProducts.emit({ data });
  }

  onInputBoxKeydown(event: KeyboardEvent, column: any): void {
    if (!column.numericOnly) {
      return;
    }
    const key = event.key;

    // Always allow control keys
    const controlKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];
    if (controlKeys.includes(key)) {return;}

    // Allow Ctrl/Cmd combos (copy, paste, select all, etc.)
    if (event.ctrlKey || event.metaKey) {return;}

    // Block minus/negative sign if minValue >= 0
    if (column.minValue !== undefined && column.minValue >= 0) {
      if (key === '-' || key === 'Minus') {
        event.preventDefault();
        return;
      }
    }

    // Allow digits
    if (/^\d$/.test(key)) {return;}

    // Allow a single decimal point
    if (key === '.') {return;}

    // Allow '%' key
    if (key === '%') {return;}

    // Block everything else (letters, special chars, other symbols)
    event.preventDefault();
  }

  onInputBoxInput(event: Event, column: any): void {
    if (!column.numericOnly) {
      return;
    }
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove all characters that are not digits, dot, or %
    // Strip invalid characters first
    value = value.replace(/[^0-9.%]/g, '');

    // Ensure only one decimal point is present
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    // Ensure '%' only appears at the end and only once
    const percentCount = (value.match(/%/g) || []).length;
    if (percentCount > 1 || (value.includes('%') && !value.endsWith('%'))) {
      // Remove all % and re-append at end if it was there
      value = value.replace(/%/g, '') + '%';
    }

    // Update the input and ngModel if value changed
    if (input.value !== value) {
      input.value = value;
      // Trigger ngModel update manually since we changed the DOM directly
      input.dispatchEvent(new Event('input'));
    }
  }

  onInputChange(element: any, column: any): void {
    if (column.minValue === undefined || column.dataKey == null) {
      return;
    }
    const raw = element[column.dataKey];
    if (raw === null || raw === undefined || raw === '') {
      return;
    }
    const str = String(raw).trim();
    const hasPercent = str.endsWith('%');
    const numStr = hasPercent ? str.slice(0, -1).trim() : str;
    const num = parseFloat(numStr);
    if (!Number.isNaN(num) && num < column.minValue) {
      element[column.dataKey] = hasPercent ? `${column.minValue}%` : String(column.minValue);
    }
  }

  onInputSelectChange(element: any, column: any): void {
    // Combine both values if needed
    element[column.dataKey] = `${element.durationValue || ''} ${element.durationUnit || ''}`.trim();


  }

  getProductTooltip(element: any): string {
    if (element.productCount > 0) {
      if (element.productNames && element.productNames.length > 0) {
        return element.productNames.map(name => `• ${name}`).join('\n');
      } else if (element.productNames && element.productNames.length === 0) {
        return 'No products found';
      } else {
        return 'Loading product names...';
      }
    }
    return '';
  }

  downloadPDF(element): void {
    this.downloadElementPDF.emit(element);
  }

  downloadXML(element): void {
    this.downloadElementXML.emit(element);
  }

  downloadJSON(element): void {
    this.downloadElementJSON.emit(element);
  }

  public hasFiveWords(value: string): boolean {
    return typeof value === 'string' && value.trim().length >= 75;
  }

  public openModal(element: any, key: string): void {
    element._visibleModals = element._visibleModals || {};
    element._visibleModals[key] = true;
  }

  public closeModal(element: any, key: string): void {
    if (element._visibleModals) {
      element._visibleModals[key] = false;
    }
  }

  public isModalVisible(element: any, key: string): boolean {
    return element._visibleModals?.[key] === true;
  }

  moveCategoryRankUp(element: any): void {
    this.rankUp.emit(element);
  }

  moveCategoryRankDown(element: any): void {
    this.rankDown.emit(element);
  }

  getPromoUrl(row: any): string {
    return `${BankIoRetailUrl.promoPage}/promo/${row.url}`;
  }

  copyPromoUrl(url: string, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!url) {
      this.toastService.open('No URL available to copy', 'Close');
      return;
    }

    const success = this.clipboard.copy(url);
    if (success) {
      this.toastService.success('Promo page URL copied!');
    } else {
      this.toastService.fail('Failed to copy URL');
    }
  }

  getCellValue(element: any, colKey: string): string {
    if (colKey === 'textValue') {
      if (element.text && element.value) {
        return `${element.text} (${element.value})`;
      } else if (element.text) {
        return element.text;
      } else if (element.value) {
        return `(${element.value})`;
      } else {
        return 'N/A';
      }
    }

    return element[colKey] || 'N/A';
  }

  shouldShowReadMore(element: any, dataKey: string): boolean {
    const text = element[dataKey] || '';
    return text.length > 30;
  }

  getTruncatedText(element: any, dataKey: string): SafeHtml {
    const text = element[dataKey] || '';
    let truncatedText = text;
    if (text.length > 30) {
      truncatedText = text.substring(0, 30);
    }
    return this.sanitizer.bypassSecurityTrustHtml(truncatedText);
  }

  // Constants for better performance
  private static readonly PAYMENT_FREQUENCY_PATTERN = /^(Monthly|Weekly|Bi-weekly|Biweekly|Semi-monthly|Quarterly|Annually|Yearly)$/i;
  private static readonly HTML_ENTITY_MAP: { [key: string]: string } = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': '\'',
    '&#x27;': '\'',
    '&#x2F;': '/',
    '&#x60;': '`',
    '&#x3D;': '='
  };
  private static readonly JOINTLY_OWNED_LINK_STYLE = 'color: #819eae; text-decoration: none; cursor: pointer; font-weight: bold;';

  sanitizeHtmlPreserveLineBreaks(html: string, element?: any): SafeHtml {
    if (!html || typeof html !== 'string') {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    // Process HTML: convert <br> to newlines, remove other tags, decode entities
    let text = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/[ \t]+/g, ' ')
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      .trim();

    // Decode HTML entities using map for better performance
    text = Object.keys(CustomTableComponent.HTML_ENTITY_MAP).reduce(
      (acc, entity) => acc.replace(new RegExp(entity, 'g'), CustomTableComponent.HTML_ENTITY_MAP[entity]),
      text
    );

    // Process lines
    const lines = text.split('\n').filter(line => line.trim());
    const processedLines = this.processDetailLines(lines);

    // Add jointly owned links if available
    const jointlyOwnedHtml = this.buildJointlyOwnedLinks(element);
    if (jointlyOwnedHtml) {
      processedLines.push(jointlyOwnedHtml);
    }

    return this.sanitizer.bypassSecurityTrustHtml(processedLines.join('<br>'));
  }

  private processDetailLines(lines: string[]): string[] {
    return lines.map((line, index) => {
      const trimmed = line.trim();
      const escaped = this.escapeHtml(trimmed);

      // First line: bold
      if (index === 0) {
        return `<strong>${escaped}</strong>`;
      }


      if (CustomTableComponent.PAYMENT_FREQUENCY_PATTERN.test(trimmed)) {
        const term = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
        return `N: ${this.escapeHtml(term)} Payment`;
      }

      return escaped;
    });
  }


  private buildJointlyOwnedLinks(element: any): string | null {
    const linked = element?.linkedMembersProspects;
    if (!Array.isArray(linked) || linked.length === 0) {
      return null;
    }

    const links = linked.map((item: any) => {
      const name = this.escapeHtml(item.fullName || '');
      const id = item.id;
      const url = item.type === 'Member'
        ? `/member-profile/${id}`
        : `/prospect-profile/${id}`;

      return `<a href="${url}" style="${CustomTableComponent.JOINTLY_OWNED_LINK_STYLE}" data-jointly-owned="true" data-href="${url}">${name}</a>`;
    });

    return `Jointly Owned ${links.join(', ')}`;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Handles clicks on links within the details column
   * Intercepts anchor tag clicks and uses Angular Router for navigation
   */
  handleDetailsLinkClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target?.tagName === 'A' && target.getAttribute('data-jointly-owned') === 'true') {
      event.preventDefault();
      event.stopPropagation();
      const href = target.getAttribute('data-href') || target.getAttribute('href');
      if (href) {
        this.router.navigate([href]);
      }
    }
  }

  onReadMoreClick(element: any, event: Event): void {
    event.stopPropagation();
    this.readMoreClick.emit(element);
  }

  onCheckboxChange(element: any) {
    setTimeout(() => {
      this.checkboxChanged.emit(element);
    });
  }

  /**
   * Strips HTML tags from a string and returns plain text
   */
  stripHtmlTags(html: string): string {
    if (!html || typeof html !== 'string') {
      return '';
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const plainText = tmp.textContent || tmp.innerText || '';
    return plainText.trim().replace(/\s+/g, ' ');
  }


  containsHtmlTags(value: string): boolean {
    if (!value || typeof value !== 'string') {
      return false;
    }
    const htmlTagPattern = /<[^>]+>/g;
    return htmlTagPattern.test(value);
  }


  shouldUseSpecialFormatting(element: any, dataKey: string): boolean {
    if (!element) {
      return false;
    }
    const value = element[dataKey];
    if (!value) {
      return false;
    }

    if (this.containsHtmlTags(value.toString())) {
      return true;
    }

    const fieldName = (element.field || '').toString().toLowerCase().trim();
    return fieldName === 'features' || fieldName === 'description';
  }


  getPlainTextValue(element: any, dataKey: string): string {
    if (!element) {
      return '';
    }
    const value = element[dataKey];
    if (value === null || value === undefined) {
      return '';
    }
    return this.stripHtmlTags(value.toString());
  }


  shouldShowViewDetails(element: any, dataKey: string): boolean {
    if (!this.shouldUseSpecialFormatting(element, dataKey)) {
      return false;
    }
    const plainText = this.getPlainTextValue(element, dataKey);
    return plainText.length > 50;
  }

    getTruncatedPlainText(element: any, dataKey: string, maxLength: number = 50): string {
    const plainText = this.getPlainTextValue(element, dataKey);
    if (plainText.length <= maxLength) {
      return plainText;
    }
    return plainText.substring(0, maxLength) + '...';
  }

  formatSourceDate(dateValue: any): string {
    if (!dateValue) {
      return '';
    }

    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return '';
      }
      // Format as MM-DD-YYYY (e.g., "02-03-2024")
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return '';
    }
  }
}
